/**
 * Color Mixing Utilities for Educational Color Theory
 * Provides additive and subtractive color mixing calculations
 * 
 * NOTE: RYB (Red-Yellow-Blue) subtractive mixing uses simplified approximations
 * for educational purposes. Real-world pigment mixing involves complex chemical
 * interactions that may differ from these mathematical models.
 */

import { colorEngine } from '../colorEngine';

export interface MixingResult {
  resultingColor: string;
  components: string[];
  mixingType: 'additive' | 'subtractive';
  ratio: number[];
  explanation: string;
  accuracy: number; // 0-100 score for how realistic the result is
}

export interface ColorMixingExercise {
  id: string;
  title: string;
  description: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  mixingType: 'additive' | 'subtractive';
  targetColor: string;
  availableColors: string[];
  solution: string[];
  explanation: string;
  hints: string[];
}

export interface MixingTechnique {
  name: string;
  description: string;
  type: 'additive' | 'subtractive';
  application: string;
  examples: string[];
  bestPractices: string[];
}

// RGB additive color mixing (light mixing)
export function mixAdditive(colors: string[], ratios: number[] = []): MixingResult {
  if (colors.length === 0) {
    return {
      resultingColor: '#000000',
      components: [],
      mixingType: 'additive',
      ratio: [],
      explanation: 'No colors provided for mixing',
      accuracy: 0
    };
  }

  // Default equal ratios if not provided
  if (ratios.length === 0) {
    ratios = colors.map(() => 1 / colors.length);
  }

  // Normalize ratios
  const totalRatio = ratios.reduce((sum, ratio) => sum + ratio, 0);
  const normalizedRatios = ratios.map(ratio => ratio / totalRatio);

  // Parse colors to RGB
  const rgbColors = colors.map(color => {
    const parsed = colorEngine.parseColor(color);
    return parsed?.rgb || { r: 0, g: 0, b: 0 };
  });

  // Mix using weighted average
  let mixedR = 0;
  let mixedG = 0;
  let mixedB = 0;

  rgbColors.forEach((rgb, index) => {
    mixedR += rgb.r * normalizedRatios[index];
    mixedG += rgb.g * normalizedRatios[index];
    mixedB += rgb.b * normalizedRatios[index];
  });

  // Convert to hex
  const resultingColor = rgbToHex(
    Math.round(mixedR),
    Math.round(mixedG),
    Math.round(mixedB)
  );

  const explanation = generateAdditiveExplanation(colors, ratios, resultingColor);
  const accuracy = calculateMixingAccuracy(colors, ratios, resultingColor, 'additive');

  // Return the mixing result
  return {
    resultingColor,
    components: colors,
    mixingType: 'additive',
    ratio: normalizedRatios,
    explanation,
    accuracy
  };
}

// RYB subtractive color mixing (pigment mixing)
export function mixSubtractive(colors: string[], ratios: number[] = []): MixingResult {
  if (colors.length === 0) {
    return {
      resultingColor: '#FFFFFF',
      components: [],
      mixingType: 'subtractive',
      ratio: [],
      explanation: 'No colors provided for mixing',
      accuracy: 0
    };
  }

  // Default equal ratios if not provided
  if (ratios.length === 0) {
    ratios = colors.map(() => 1 / colors.length);
  }

  // Normalize ratios
  const totalRatio = ratios.reduce((sum, ratio) => sum + ratio, 0);
  const normalizedRatios = ratios.map(ratio => ratio / totalRatio);

  // Convert to RYB space for subtractive mixing
  const rybColors = colors.map(color => rgbToRyb(color));

  // Mix in RYB space
  let mixedR = 0;
  let mixedY = 0;
  let mixedB = 0;

  rybColors.forEach((ryb, index) => {
    mixedR += ryb.r * normalizedRatios[index];
    mixedY += ryb.y * normalizedRatios[index];
    mixedB += ryb.b * normalizedRatios[index];
  });

  // Convert back to RGB
  const rgbResult = rybToRgb(mixedR, mixedY, mixedB);
  const resultingColor = rgbToHex(rgbResult.r, rgbResult.g, rgbResult.b);

  const explanation = generateSubtractiveExplanation(colors, ratios, resultingColor);
  const accuracy = calculateMixingAccuracy(colors, ratios, resultingColor, 'subtractive');

  return {
    resultingColor,
    components: colors,
    mixingType: 'subtractive',
    ratio: normalizedRatios,
    explanation,
    accuracy
  };
}

// Convert RGB to RYB color space
function rgbToRyb(color: string): { r: number; y: number; b: number } {
  const parsed = colorEngine.parseColor(color);
  if (!parsed?.rgb) return { r: 0, y: 0, b: 0 };

  const { r, g, b } = parsed.rgb;
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  // Simplified RGB to RYB conversion
  const rY = Math.max(0, Math.min(1, rn - bn * 0.5));
  const yY = Math.max(0, Math.min(1, gn - rn * 0.3 - bn * 0.3));
  const bY = Math.max(0, Math.min(1, bn - gn * 0.5));

  return { r: rY, y: yY, b: bY };
}

// Convert RYB to RGB color space
function rybToRgb(r: number, y: number, b: number): { r: number; g: number; b: number } {
  // Simplified RYB to RGB conversion
  const rn = Math.max(0, Math.min(1, r + y * 0.5));
  const gn = Math.max(0, Math.min(1, y + r * 0.3 + b * 0.3));
  const bn = Math.max(0, Math.min(1, b + y * 0.5));

  return {
    r: Math.round(rn * 255),
    g: Math.round(gn * 255),
    b: Math.round(bn * 255)
  };
}

// Convert RGB to hex
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}

// Generate explanation for additive mixing
function generateAdditiveExplanation(colors: string[], ratios: number[], result: string): string {
  if (colors.length === 1) {
    return `Single color ${colors[0]} remains unchanged in additive mixing.`;
  }

  const colorNames = colors.map(c => getColorName(c)).join(' + ');
  const ratioDesc = ratios.length > 1 
    ? ` in ratios ${ratios.map(r => Math.round(r * 100) + '%').join(' : ')}`
    : '';

  return `Additive mixing combines ${colorNames}${ratioDesc} to create ${result}. This simulates light mixing where colors add together to become brighter.`;
}

// Generate explanation for subtractive mixing
function generateSubtractiveExplanation(colors: string[], ratios: number[], result: string): string {
  if (colors.length === 1) {
    return `Single pigment ${colors[0]} remains unchanged in subtractive mixing.`;
  }

  const colorNames = colors.map(c => getColorName(c)).join(' + ');
  const ratioDesc = ratios.length > 1 
    ? ` in ratios ${ratios.map(r => Math.round(r * 100) + '%').join(' : ')}`
    : '';

  return `Subtractive mixing combines pigments ${colorNames}${ratioDesc} to create ${result}. This simulates paint mixing where colors absorb light and become darker.`;
}

// Get color name from hex value
function getColorName(hex: string): string {
  const colorNames: Record<string, string> = {
    '#FF0000': 'Red',
    '#00FF00': 'Green', 
    '#0000FF': 'Blue',
    '#FFFF00': 'Yellow',
    '#FF00FF': 'Magenta',
    '#00FFFF': 'Cyan',
    '#FFA500': 'Orange',
    '#800080': 'Purple',
    '#FFC0CB': 'Pink',
    '#000000': 'Black',
    '#FFFFFF': 'White',
    '#808080': 'Gray'
  };

  return colorNames[hex.toUpperCase()] || hex;
}

// Calculate mixing accuracy (how realistic the result is)
function calculateMixingAccuracy(colors: string[], ratios: number[], result: string, type: 'additive' | 'subtractive'): number {
  // Simplified accuracy calculation based on color harmony principles
  let accuracy = 100;

  // Penalize mixing too many colors
  if (colors.length > 3) {
    accuracy -= (colors.length - 3) * 10;
  }

  // Penalize extreme ratios
  const maxRatio = Math.max(...ratios);
  const minRatio = Math.min(...ratios);
  if (maxRatio / minRatio > 10) {
    accuracy -= 15;
  }

  // Check for complementary color mixing in subtractive mode
  if (type === 'subtractive' && colors.length === 2) {
    const hsl1 = colorEngine.parseColor(colors[0])?.hsl;
    const hsl2 = colorEngine.parseColor(colors[1])?.hsl;
    if (hsl1 && hsl2) {
      const hueDiff = Math.abs(hsl1.h - hsl2.h);
      if (hueDiff > 150 && hueDiff < 210) {
        accuracy += 10; // Bonus for complementary mixing
      }
    }
  }

  return Math.max(0, Math.min(100, accuracy));
}

// Predict mixing outcome
export function predictMixingOutcome(baseColor: string, addedColor: string, ratio: number, type: 'additive' | 'subtractive'): MixingResult {
  const colors = [baseColor, addedColor];
  const ratios = [1 - ratio, ratio];
  
  return type === 'additive' ? mixAdditive(colors, ratios) : mixSubtractive(colors, ratios);
}

// Get mixing exercises
export function getMixingExercises(): ColorMixingExercise[] {
  return [
    // Beginner exercises
    {
      id: 'additive-primary',
      title: 'Mix Primary Colors',
      description: 'Mix red and green light to create yellow',
      difficulty: 'beginner',
      mixingType: 'additive',
      targetColor: '#FFFF00',
      availableColors: ['#FF0000', '#00FF00', '#0000FF'],
      solution: ['#FF0000', '#00FF00'],
      explanation: 'In additive mixing, red + green = yellow. This is how computer screens and TVs create colors.',
      hints: ['Think about light mixing', 'Red and green make yellow', 'Try equal amounts']
    },
    {
      id: 'subtractive-primary',
      title: 'Mix Primary Pigments',
      description: 'Mix red and blue paint to create purple',
      difficulty: 'beginner',
      mixingType: 'subtractive',
      targetColor: '#800080',
      availableColors: ['#FF0000', '#FFFF00', '#0000FF'],
      solution: ['#FF0000', '#0000FF'],
      explanation: 'In subtractive mixing, red + blue = purple. This is how artists mix paints.',
      hints: ['Think about paint mixing', 'Red and blue make purple', 'Try equal amounts']
    },
    // Intermediate exercises
    {
      id: 'additive-secondary',
      title: 'Create Secondary Colors',
      description: 'Mix appropriate primary colors to create orange',
      difficulty: 'intermediate',
      mixingType: 'additive',
      targetColor: '#FFA500',
      availableColors: ['#FF0000', '#00FF00', '#0000FF', '#FFFF00'],
      solution: ['#FF0000', '#FFFF00'],
      explanation: 'Orange is created by mixing red and yellow light in additive mixing.',
      hints: ['Orange contains red', 'Think about sunset colors', 'Try red + yellow']
    },
    {
      id: 'subtractive-tertiary',
      title: 'Create Tertiary Colors',
      description: 'Mix primary and secondary pigments to create brown',
      difficulty: 'intermediate',
      mixingType: 'subtractive',
      targetColor: '#8B4513',
      availableColors: ['#FF0000', '#FFFF00', '#0000FF', '#800080', '#008000'],
      solution: ['#FF0000', '#FFFF00', '#0000FF'],
      explanation: 'Brown is created by mixing all three primary pigments in subtractive mixing.',
      hints: ['Brown is a dark color', 'Try mixing all three primaries', 'Think about mud']
    },
    // Advanced exercises
    {
      id: 'additive-white',
      title: 'Create White Light',
      description: 'Mix the three primary colors of light to create white',
      difficulty: 'advanced',
      mixingType: 'additive',
      targetColor: '#FFFFFF',
      availableColors: ['#FF0000', '#00FF00', '#0000FF'],
      solution: ['#FF0000', '#00FF00', '#0000FF'],
      explanation: 'Equal amounts of red, green, and blue light create white light in additive mixing.',
      hints: ['White contains all colors', 'Think about rainbows', 'Mix all three primaries equally']
    },
    {
      id: 'subtractive-black',
      title: 'Create Black Pigment',
      description: 'Mix complementary pigments to create dark brown/black',
      difficulty: 'advanced',
      mixingType: 'subtractive',
      targetColor: '#2F4F4F',
      availableColors: ['#FF0000', '#FFFF00', '#0000FF', '#00FF00', '#FF00FF', '#00FFFF'],
      solution: ['#FF0000', '#00FF00', '#0000FF'],
      explanation: 'Mixing complementary pigments absorbs most light, creating very dark colors approaching black.',
      hints: ['Black absorbs all light', 'Mix complementary colors', 'Think about dark mud']
    }
  ];
}

// Get mixing techniques
export function getMixingTechniques(): MixingTechnique[] {
  return [
    {
      name: 'Direct Mixing',
      description: 'Combine colors directly on the palette or canvas',
      type: 'subtractive',
      application: 'Oil painting, acrylic painting',
      examples: ['Red + Blue = Purple', 'Yellow + Blue = Green', 'Red + Yellow = Orange'],
      bestPractices: [
        'Start with lighter color and add darker gradually',
        'Mix thoroughly to avoid streaks',
        'Clean brush between different color mixes'
      ]
    },
    {
      name: 'Layering (Glazing)',
      description: 'Apply thin transparent layers of color',
      type: 'subtractive',
      application: 'Watercolor, oil glazing techniques',
      examples: ['Yellow glaze over blue = green', 'Red glaze over yellow = orange'],
      bestPractices: [
        'Let each layer dry completely',
        'Use transparent pigments for best results',
        'Build up color gradually with multiple thin layers'
      ]
    },
    {
      name: 'Optical Mixing',
      description: 'Colors blend visually when placed close together',
      type: 'additive',
      application: 'Pointillism, digital displays, weaving',
      examples: ['Red and blue dots appear purple from distance', 'RGB pixels create full color images'],
      bestPractices: [
        'Use small, distinct color elements',
        'Consider viewing distance for effect',
        'Balance color proportions carefully'
      ]
    },
    {
      name: 'Wet-on-Wet',
      description: 'Apply wet paint into wet paint for soft blends',
      type: 'subtractive',
      application: 'Watercolor, oil painting',
      examples: ['Blue into wet yellow creates soft green transitions'],
      bestPractices: [
        'Work quickly before paint dries',
        'Use appropriate paper or surface',
        'Control moisture for desired softness'
      ]
    }
  ];
}

// Calculate color harmony score for mixed result
export function calculateColorHarmony(mixedColor: string, targetColor: string): number {
  const mixedHsl = colorEngine.parseColor(mixedColor)?.hsl;
  const targetHsl = colorEngine.parseColor(targetColor)?.hsl;
  
  if (!mixedHsl || !targetHsl) return 0;

  // Calculate hue difference
  const hueDiff = Math.abs(mixedHsl.h - targetHsl.h);
  const normalizedHueDiff = Math.min(hueDiff, 360 - hueDiff);

  // Calculate lightness difference
  const lightnessDiff = Math.abs(mixedHsl.l - targetHsl.l);

  // Calculate saturation difference
  const saturationDiff = Math.abs(mixedHsl.s - targetHsl.s);

  // Score based on similarity (0-100)
  const hueScore = Math.max(0, 100 - normalizedHueDiff);
  const lightnessScore = Math.max(0, 100 - lightnessDiff * 2);
  const saturationScore = Math.max(0, 100 - saturationDiff * 2);

  return (hueScore + lightnessScore + saturationScore) / 3;
}
